import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faCheckCircle,
  faTimesCircle,
  faCertificate,
  faUser,
  faListCheck,
  faFingerprint
} from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../context/AuthContext';
import { API } from '../../global/global';

interface VerificacionData {
  valido: boolean;
  mensaje?: string;
  integridad_documento?: boolean;
  documento_autentico?: boolean;
  informacion_evaluado?: {
    nombre_completo: string;
    rfc: string;
    empresa: string;
  };
  certificacion?: {
    numero_certificacion: string;
    fecha_emision: string;
    valida_hasta: string;
    version_documento: number;
  };
  reporte_final?: {
    recomendacion: string;
    nota_3rfp: string;
    evaluacion_2rfp: string;
  };
  plataforma_certificadora?: {
    nombre: string;
    empresa: string;
    version_sistema: string;
  };
  proceso_evaluacion?: {
    fecha_inicio: string;
    fecha_finalizacion: string;
    duracion_dias: number;
  };
  modulos_evaluados?: Array<{
    nombre: string;
    fecha_evaluacion: string | null;
    completado: boolean;
  }>;
  trazabilidad?: {
    total_modulos: number;
    modulos_completados: number;
    hash_original: string;
    fecha_verificacion: string;
  };
  advertencias?: string[];
}

export const VerificacionDocumento: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  // El hash puede incluir un sub-path (ej. "presencial/{hash}"), por eso se extrae
  // directamente del pathname en vez de useParams.
  const hash = decodeURIComponent(location.pathname.replace('/verificar/', ''));

  // Este endpoint devuelve el objeto de verificación en la raíz del JSON (no envuelto
  // en { data: ... } como el resto de la API), por eso no se usa useGetData aquí.
  const fetchVerificacion = async (): Promise<VerificacionData> => {
    try {
      const response = await axios.get<VerificacionData>(`${API}/verificar/${hash}`, {
        headers: { Authorization: 'Bearer ' + authContext.token },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message === 'Unauthenticated.') {
          authContext.logout();
          toast.error('Tu sesión ha expirado.', { toastId: 'errorMessage' });
        } else if (error.response?.status === 403) {
          navigate('/');
          toast.error('No tienes permiso de realizar esta acción.', { toastId: 'errorMessage' });
        } else if (error.response?.data) {
          // Respuesta de negocio conocida (ej. hash no encontrado): { valido: false, mensaje: ... }
          return error.response.data as VerificacionData;
        }
      }
      throw error;
    }
  };

  const { data: verificacion, isLoading, isError } = useQuery<VerificacionData>({
    queryKey: ['verificacion-documento', hash],
    queryFn: fetchVerificacion,
    enabled: !!hash,
  });

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Estilos inline
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '2rem 1rem',
      fontFamily: 'Arial, sans-serif'
    },
    card: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    header: {
      textAlign: 'center' as const,
      padding: '2rem',
      borderBottom: '1px solid #e5e7eb'
    },
    content: {
      padding: '2rem'
    },
    statusValid: {
      display: 'flex',
      alignItems: 'center',
      padding: '1rem',
      backgroundColor: '#d1fae5',
      borderRadius: '8px',
      marginBottom: '2rem'
    },
    statusInvalid: {
      display: 'flex',
      alignItems: 'center',
      padding: '1rem',
      backgroundColor: '#fee2e2',
      borderRadius: '8px',
      marginBottom: '2rem'
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    infoSection: {
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    modulesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '1rem'
    },
    moduleItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #e5e7eb'
    },
    loading: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh'
    },
    spinner: {
      width: '40px',
      height: '40px',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>
            Verificando documento...
          </p>
        </div>
      </div>
    );
  }

  if (isError || !verificacion || !verificacion.valido) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <FontAwesomeIcon
              icon={faShieldAlt}
              style={{ fontSize: '3rem', color: '#dc2626', marginBottom: '1rem' }}
            />
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', margin: 0 }}>
              Verificación de Documento
            </h1>
          </div>
          <div style={styles.content}>
            <div style={styles.statusInvalid}>
              <FontAwesomeIcon
                icon={faTimesCircle}
                style={{ fontSize: '1.5rem', color: '#dc2626', marginRight: '0.5rem' }}
              />
              <div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#991b1b', margin: 0 }}>
                  Documento No Válido
                </h2>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  {verificacion?.mensaje || 'Documento no encontrado o hash inválido'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <FontAwesomeIcon
            icon={faShieldAlt}
            style={{ fontSize: '3rem', color: '#059669', marginBottom: '1rem' }}
          />
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            Verificación de Documento
          </h1>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0' }}>
            Sistema de Certificación SkyClear - SkyAngel
          </p>
        </div>

        <div style={styles.content}>
          {/* Status */}
          <div style={styles.statusValid}>
            <FontAwesomeIcon
              icon={faCheckCircle}
              style={{ fontSize: '1.5rem', color: '#059669', marginRight: '0.5rem' }}
            />
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#166534', margin: 0 }}>
                Documento Válido y Auténtico
              </h2>
              <p style={{ color: '#6b7280', margin: 0 }}>
                Este documento ha sido verificado exitosamente
              </p>
            </div>
          </div>

          {/* Información */}
          <div style={styles.infoGrid}>
            {/* Información del Evaluado */}
            <div style={styles.infoSection}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem', color: '#3b82f6' }} />
                Información del Evaluado
              </h3>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>Nombre:</strong> {verificacion.informacion_evaluado?.nombre_completo || 'No disponible'}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>RFC:</strong> {verificacion.informacion_evaluado?.rfc || 'No disponible'}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>Empresa:</strong> {verificacion.informacion_evaluado?.empresa || 'No disponible'}
              </p>
            </div>

            {/* Información de la Certificación */}
            <div style={styles.infoSection}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                <FontAwesomeIcon icon={faCertificate} style={{ marginRight: '0.5rem', color: '#059669' }} />
                Certificación
              </h3>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>Número:</strong> {verificacion.certificacion?.numero_certificacion || 'No disponible'}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>Fecha de Emisión:</strong> {verificacion.certificacion?.fecha_emision ? formatearFecha(verificacion.certificacion.fecha_emision) : 'No disponible'}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>Válida hasta:</strong> {verificacion.certificacion?.valida_hasta ? formatearFecha(verificacion.certificacion.valida_hasta) : 'No disponible'}
              </p>
              <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                <strong>Versión:</strong> {verificacion.certificacion?.version_documento || 'No disponible'}
              </p>
            </div>
          </div>

          {/* Módulos Evaluados */}
          <div style={styles.infoSection}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faFingerprint} style={{ marginRight: '0.5rem', color: '#9333ea' }} />
              Trazabilidad
            </h3>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faListCheck} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
              Módulos Evaluados
              <span style={{
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#059669',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                {verificacion.trazabilidad?.modulos_completados || 0} de {verificacion.trazabilidad?.total_modulos || 0}
              </span>
            </h3>
            <div style={styles.modulesGrid}>
              {verificacion.modulos_evaluados?.map((modulo: any, index: number) => (
                <div key={index} style={styles.moduleItem}>
                  <div>
                    <p style={{ fontWeight: '500', color: '#111827', margin: 0 }}>
                      {modulo.nombre}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                      {modulo.fecha_evaluacion ? formatearFecha(modulo.fecha_evaluacion) : 'Fecha no disponible'}
                    </p>
                  </div>
                  <FontAwesomeIcon
                    icon={modulo.completado ? faCheckCircle : faTimesCircle}
                    style={{ color: modulo.completado ? '#059669' : '#dc2626' }}
                  />
                </div>
              )) || []}
            </div>
          </div>

          {/* Trazabilidad */}
          <div style={styles.infoSection}>
            <p style={{ margin: '0.5rem 0', color: '#374151', fontSize: '0.875rem' }}>
              <strong>Verificado el:</strong> {verificacion.trazabilidad?.fecha_verificacion ? formatearFecha(verificacion.trazabilidad.fecha_verificacion) : 'No disponible'}
            </p>
          </div>

          {/* Footer */}
          <div style={{
            textAlign: 'center',
            marginTop: '2rem',
            padding: '1rem',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px'
          }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
              Este documento ha sido verificado mediante el sistema de certificación digital de SkyClear.
              <br />
              Para más información, contacte a SkyAngel.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificacionDocumento;
